<template>
  <div class="ma-4 px-4">
    <searchBarFilter
      @add="openAddDialog"
      @updateSearch="(v) => (search = v)"
      @uploadButton="showUploadDialog = true"
      :downloadButton="true"
      :uploadCsvButton="true"
      :downloadUrl="'download_discounts'"
      headerText="Discount"
      class="mt-2"
    />

    <v-row>
      <v-col cols="12">
        <v-sheet outlined>
          <v-data-table
            :headers="headers"
            :search="search"
            :items="discounts"
            fixed-header
            class="secondary row-height-50"
            :loading="tableLoading"
            loading-text="Loading discounts..."
          >
            <template #[`item.action`]="{ item }">
              <v-icon small class="mr-2" color="accent" @click="edit(item)">
                mdi-pencil
              </v-icon>
            </template>
            <template #[`item.dob`]="{ item }">
              {{ formattedDob(item.dob) }}
            </template>
            <template #[`item.batch`]="{ item }">
              {{ item.year + " -" + item.batch }}
            </template>
            <template #[`item.is_discount_applied`]="{ item }">
              {{ item.is_discount_applied ? "Yes" : "No" }}
            </template>
          </v-data-table>
        </v-sheet>
      </v-col>
    </v-row>

    <!-- Form Dialog -->
    <v-dialog v-model="dialog" width="800" persistent scrollable>
      <formDialog
        v-if="dialog"
        @closeDialog="closeDialog"
        @create="createDiscount"
        @update="updateDiscount"
        :discount="discount"
        :categories="categories"
        :edit="editMode"
      />
    </v-dialog>

    <!-- Upload Dialog -->
    <v-dialog v-model="showUploadDialog" max-width="600px" persistent>
      <v-card>
        <v-card-title class="font-weight-medium secondary--text primary">
          Upload Discounts CSV
          <v-spacer></v-spacer>
          <v-btn small icon @click="showUploadDialog = false">
            <v-icon small color="secondary">mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-divider></v-divider>

        <v-card-text class="pt-4">
          <!-- Instructions -->
          <div class="mb-4">
            <h4 class="primary--text mb-2">📌 CSV Format Instructions</h4>
            <v-sheet class="pa-3 grey lighten-4 rounded">
              <p class="font-weight-medium mb-2">
                Required Columns (in order):
              </p>
              <ol class="pl-3">
                <li>
                  <strong>Name</strong> - Full name of the person (required)
                </li>
                <li>
                  <strong>Mobile No</strong> - 10 digit mobile number without
                  country code (required)
                </li>
                <li>
                  <strong>DOB</strong> - Date of birth in DD-MM-YYYY format
                  (required)
                </li>
                <li><strong>Email</strong> - Valid email address, preferably personal email (required)</li>
                <li>
                  <strong>Year</strong> - Enter the applicable year (required)
                </li>
                <li>
                  <strong>Batch</strong> - Enter the applicable batch (required)
                </li>
                <li>
                  <strong>Category</strong> - Discount category (required)
                </li>
                <li>
                  <strong>Amount</strong> - Discount amount in numbers
                  (required)
                </li>
              </ol>

              <div class="pt-2 d-flex justify-center">
                <v-btn  color="primary" @click="downloadTemplate">
                <v-icon left>mdi-download</v-icon>
                Download Template
              </v-btn>
              </div>

              <v-alert type="info" dense class="mt-3 mb-0">
                <small>
                  <strong>Note:</strong>
                  If any errors occur during the upload process, a CSV file containing the errors will be automatically downloaded. Please review the CSV file, correct any errors, and re-upload the file.
                </small>
              </v-alert>
            </v-sheet>
          </div>

          <!-- File Upload -->
          <v-file-input
            ref="fileInput"
            v-model="csvFile"
            accept=".csv"
            label="Select CSV File"
            outlined
            dense
            prepend-icon="mdi-file-document"
            :loading="uploadLoading"
            :disabled="uploadLoading"
            @change="validateFile"
            show-size
            counter
          />

          <v-alert v-if="fileError" type="error" dense class="mt-2">
            {{ fileError }}
          </v-alert>
        </v-card-text>

        <v-card-actions class="pt-0 pb-4 px-4">
          <v-spacer></v-spacer>
          <v-btn @click="showUploadDialog = false" :disabled="uploadLoading">
            Cancel
          </v-btn>
          <v-btn
            color="primary"
            @click="uploadCSV"
            :loading="uploadLoading"
            :disabled="!csvFile || !!fileError"
          >
            Upload
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Confirm Dialog for Status Change -->
    <ConfirmDialog
      v-if="showDialog"
      :show="showDialog"
      @delete="changeConfirmation"
      @close="closeConfirmDialog"
      title="discount"
      :name="currentItem.name"
      :active="currentItem.active"
      :loading="actionLoading"
    />

    <!-- Discount Details Dialog -->
    <v-dialog v-model="showDataDialog" max-width="600" persistent scrollable>
      <v-card class="secondary">
        <v-card-title class="primary white--text">
          {{ selectedItem.name }}
          <v-spacer></v-spacer>
          <v-btn small icon @click="showDataDialog = false">
            <v-icon small color="secondary">mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-divider></v-divider>
        <v-card-text class="mt-3 reset-card-text">
          <p><strong>Mobile:</strong> {{ selectedItem.mobile_no }}</p>
          <p><strong>Email:</strong> {{ selectedItem.email }}</p>
          <p><strong>Category:</strong> {{ selectedItem.category }}</p>
          <p><strong>Amount:</strong> ₹{{ selectedItem.amount }}</p>
          <p>
            <strong>Date of Birth:</strong> {{ formattedDob(selectedItem.dob) }}
          </p>
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- Upload Error Dialog -->
    <v-dialog v-model="uploadErrorDialog" width="450" persistent>
      <uploadError
        v-if="uploadErrorDialog"
        :errorCsvBlob="errorCsvBlob"
        @downloaded="
          initialise, (uploadErrorDialog = false), (errorCsvBlob = null)
        "
        csvTitle="Discount"
      />
    </v-dialog>

    <!-- Global loading overlay -->
    <v-overlay :value="globalLoading" opacity="0.8" z-index="1000">
      <v-progress-circular indeterminate size="64" color="primary">
        Processing...
      </v-progress-circular>
    </v-overlay>
  </div>
</template>

<script>
import formDialog from "@/components/discounts/form.vue";
import searchBarFilter from "@/components/searchBarFilter.vue";
import { studentDiscountHeaders } from "@/constants/headers";
import ConfirmDialog from "@/components/confirm.vue";
import uploadError from "@/components/uploadError.vue";

export default {
  components: {
    formDialog,
    searchBarFilter,
    ConfirmDialog,
    uploadError,
  },
  data() {
    return {
      dialog: false,
      editMode: false,
      showDialog: false,
      currentItem: {},
      discounts: [],
      discount: {},
      categories: [],
      search: "",
      headers: studentDiscountHeaders,
      selectedItem: {
        name: "",
        mobile_no: "",
        email: "",
        category: "",
        amount: null,
        dob: "",
      },
      showDataDialog: false,
      uploadErrorDialog: false,
      errorCsvBlob: null,

      // Upload states
      showUploadDialog: false,
      csvFile: null,
      uploadLoading: false,
      fileError: null,

      // Loader states
      tableLoading: false,
      actionLoading: false,
      globalLoading: false,
    };
  },
  created() {
    this.$store.commit("setBreadcrumbs", [{ text: "Discounts" }]);
    this.initialise();
  },
  methods: {
    initialise() {
      this.getDiscounts();
      this.getUniqueCategories();
    },

    async getDiscounts() {
      this.tableLoading = true;
      try {
        const res = await this.$axios.get("discounts");
        const discounts = res.data.data;
        this.discounts = discounts.sort((a, b) => b.id - a.id);
      } catch (error) {
        this.$store.commit("showSnackbar", {
          message: error.response?.data?.message || "Failed to fetch discounts",
          color: "error",
        });
      } finally {
        this.tableLoading = false;
      }
    },

    async getUniqueCategories(){
        const { data } = await this.$axios.get('/get_unique_categories_in_discount');
        this.categories = data;
    },

    async createDiscount(data) {
      this.globalLoading = true;
      try {
        await this.$axios.post("discounts", { discounts: data });
        this.$store.commit("showSnackbar", {
          message: "Discount created successfully",
          color: "success",
        });
        this.closeDialog();
        await this.initialise();
      } catch (error) {
        const errorMessage = await this.$store.dispatch(
          "throwError",
          error.response,
        );
        this.$store.commit("showSnackbar", {
          message: errorMessage,
          color: "error",
        });
        throw error;
      } finally {
        this.globalLoading = false;
      }
    },

    async updateDiscount(data) {
      this.globalLoading = true;
      try {
        await this.$axios.put("discounts/" + data.id, { discounts: data });
        this.$store.commit("showSnackbar", {
          message: "Discount updated successfully",
          color: "success",
        });
        this.closeDialog();
        await this.getDiscounts();
      } catch (error) {
        const errorMessage = await this.$store.dispatch(
          "throwError",
          error.response,
        );
        this.$store.commit("showSnackbar", {
          message: errorMessage,
          color: "error",
        });
        throw error;
      } finally {
        this.globalLoading = false;
      }
    },

    async changeConfirmation() {
      this.actionLoading = true;
      const updatedDiscount = {
        id: this.currentItem.id,
        active: !this.currentItem.active,
      };

      try {
        await this.$axios.put("discounts/" + updatedDiscount.id, {
          discounts: updatedDiscount,
        });
        this.$store.commit("showSnackbar", {
          message: `Discount ${
            updatedDiscount.active ? "activated" : "deactivated"
          } successfully`,
          color: "success",
        });
        this.closeConfirmDialog();
        await this.getDiscounts();
      } catch (error) {
        const errorMessage = await this.$store.dispatch(
          "throwError",
          error.response,
        );
        this.$store.commit("showSnackbar", {
          message: errorMessage,
          color: "error",
        });
      } finally {
        this.actionLoading = false;
      }
    },

    downloadTemplate() {
      const headers = [
        "Name",
        "Mobile No",
        "Date of Birth",
        "Email",
        "Year",
        "Batch",
        "Category",
        "Amount",
      ];

      const csvContent = [headers.join(",")].join("\n");

      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const link = document.createElement("a");
      const url = URL.createObjectURL(blob);
      link.href = url;
      link.setAttribute("download", "discounts_template.csv");
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      this.$store.commit("showSnackbar", {
        message: "Template downloaded successfully",
        color: "success",
      });
    },

    validateFile(file) {
      this.fileError = null;

      if (!file) return;

      // Check file extension
      if (!file.name.endsWith(".csv")) {
        this.fileError = "Please upload a CSV file";
        this.csvFile = null;
        return;
      }

      // Check file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        this.fileError = "File size should be less than 5MB";
        this.csvFile = null;
        return;
      }
    },

    async uploadCSV() {
      if (!this.csvFile) return;

      this.uploadLoading = true;
      this.fileError = null;

      const formData = new FormData();
      formData.append("csv_file", this.csvFile);

      try {
        const response = await this.$axios.post("upload_discounts", formData, {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        });

        if (response.status === 201) {
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement("a");
            link.href = url;
            link.setAttribute(
              "download",
              `Discounts Upload Errors.csv`,
            ); 
            document.body.appendChild(link);
            link.click(); 
            document.body.removeChild(link);
            return;
          }

        this.$store.commit("showSnackbar", {
          message: response.data.message || "Discounts uploaded successfully",
          color: "success",
        });

        // Close dialog and refresh data
        this.showUploadDialog = false;
        this.initialise();
        this.csvFile = null;
        await this.getDiscounts();
      } catch (error) {
        if (error.response?.data?.errors) {
          // If server returns error CSV
          const errorCsv = error.response.data.errors;
          this.errorCsvBlob = new Blob([errorCsv]);
          this.uploadErrorDialog = true;
        } else {
          const errorMessage = await this.$store.dispatch(
            "throwError",
            error.response,
          );
          this.$store.commit("showSnackbar", {
            message: errorMessage || "Upload failed",
            color: "error",
          });
        }
      } finally {
        this.uploadLoading = false;
      }
    },

    openAddDialog() {
      this.editMode = false;
      this.discount = {};
      this.dialog = true;
    },

    openConfirm(item) {
      this.showDialog = true;
      this.currentItem = { ...item };
    },

    closeConfirmDialog() {
      this.showDialog = false;
      this.currentItem = {};
    },

    edit(item) {
      this.discount = { ...item };
      this.editMode = true;
      this.dialog = true;
    },

    closeDialog() {
      this.dialog = false;
      this.editMode = false;
      this.discount = {};
    },

    showDiscountDetails(item) {
      this.selectedItem = { ...item };
      this.showDataDialog = true;
    },

    formattedDob(dob) {
      if (!dob) return "";
      const [year, month, day] = dob.split("-");
      return `${day}-${month}-${year}`;
    },
  },
};
</script>

<style scoped>
.reset-card-text {
  font-size: medium !important;
  font-weight: normal !important;
  line-height: normal !important;
  letter-spacing: normal !important;
}
</style>
